import React from 'react';
import SectionLabel from 'components/controls/sectionLabel';
import { BhFlowNode, BhFlowTitle, BhFlowSubtitle, BhFlowTags, BhFlowTag }
  from 'blob_types/blobhub/compute/workflow/flowkit/flowNode';
import StatusPill from '../../components/statusPill';

/**
 * MissionBoard — the mission's cells grouped by what is happening to them.
 *
 * The graph answers "what is the shape of this work"; the lanes answer "what needs me, now". The DAG
 * survives the loss of the edges as one `↳ after <id>` chip per prerequisite, which is why a lane is
 * ordered by prerequisite depth: inside a lane, a chip points at a card above it.
 */

// The five lanes, in the order work moves through them, covering every entry in schema.js's
// STATUSES. Two of them hold two statuses, which is why a card keeps its own pill.
export const LANES = [
  { key: 'draft', title: 'Draft', statuses: ['draft'] },
  { key: 'waiting', title: 'Waiting', statuses: ['planned', 'inactive'] },
  { key: 'running', title: 'Running', statuses: ['working'] },
  { key: 'attention', title: 'Needs you', statuses: ['input_needed', 'failed'] },
  { key: 'done', title: 'Done', statuses: ['completed'] },
];

// Where a status this build has never seen goes. The orchestrator owns the vocabulary, so a value
// added tomorrow has to render today — absent from the board is the one answer that loses a cell.
export const OTHER_LANE = { key: 'other', title: 'Other', statuses: [] };

// The same `status || 'draft'` default CellNode applies, so the two views of one cell cannot
// disagree about what it is.
export function cellStatus(cell) {
  return ((cell && cell.props) || {}).status || 'draft';
}

export function laneKeyOf(cell) {
  const status = cellStatus(cell);
  const lane = LANES.find(entry => entry.statuses.includes(status));
  return lane ? lane.key : OTHER_LANE.key;
}

/**
 * The longest chain of prerequisites reaching each cell. Ordering only, so a cycle — which nothing
 * in the graph object forbids — must cost a depth rather than a hang: the walk abandons an id it is
 * already inside.
 * @param {Array} cells
 * @param {Array} edges - { from_id: dependent, to_id: prerequisite }
 * @returns {Map<string, number>}
 */
export function prerequisiteDepth(cells, edges) {
  const prerequisites = new Map(cells.map(cell => [cell.element_id, []]));
  for (const edge of edges || []) {
    if (prerequisites.has(edge.from_id)) prerequisites.get(edge.from_id).push(edge.to_id);
  }
  const depth = new Map();
  const walk = (id, inside) => {
    if (depth.has(id)) return depth.get(id);
    if (inside.has(id)) return 0;
    inside.add(id);
    let longest = 0;
    for (const prerequisite of prerequisites.get(id) || []) {
      longest = Math.max(longest, walk(prerequisite, inside) + 1);
    }
    inside.delete(id);
    depth.set(id, longest);
    return longest;
  };
  for (const cell of cells) walk(cell.element_id, new Set());
  return depth;
}

/**
 * The lanes to draw: the five named ones always, and Other only when something is in it.
 * @param {Array} cells
 * @param {Array} edges
 * @returns {Array<{key: string, title: string, statuses: string[], cells: Array}>}
 */
export function buildLanes(cells, edges) {
  const list = cells || [];
  const depth = prerequisiteDepth(list, edges);
  const arrived = new Map(list.map((cell, index) => [cell.element_id, index]));
  const byKey = new Map();
  for (const cell of list) {
    const key = laneKeyOf(cell);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(cell);
  }
  for (const grouped of byKey.values()) {
    grouped.sort((a, b) =>
      ((depth.get(a.element_id) || 0) - (depth.get(b.element_id) || 0)) ||
      (arrived.get(a.element_id) - arrived.get(b.element_id)));
  }
  const lanes = LANES.map(lane => ({ ...lane, cells: byKey.get(lane.key) || [] }));
  const other = byKey.get(OTHER_LANE.key) || [];
  if (other.length) lanes.push({ ...OTHER_LANE, cells: other });
  return lanes;
}

// Which cells each cell depends on, as a lookup the cards read.
function prerequisiteIndex(cells, edges) {
  const index = new Map((cells || []).map(cell => [cell.element_id, []]));
  for (const edge of edges || []) {
    if (index.has(edge.from_id)) index.get(edge.from_id).push(edge.to_id);
  }
  return index;
}

/**
 * One card. It is the SAME flow-kit card the graph draws — the fixed 240px is scoped to
 * `.mc-graph-canvas`, so the identical class fills a lane here — rather than a second card that can
 * drift from the first. The pill stays even though the lane names a status, because two lanes name
 * two.
 */
function CellCard({ cell, prerequisites, selected, onSelect, onOpen }) {
  const id = cell.element_id;
  const props = cell.props || {};
  return (
    <button
      type="button"
      className="mc-lane-card"
      onClick={() => onSelect(id)}
      onDoubleClick={() => onOpen(id)}
    >
      <BhFlowNode selected={selected} className="mc-cell-node">
        <div className="mc-cell-node-head">
          <BhFlowSubtitle>{id}</BhFlowSubtitle>
          <StatusPill status={cellStatus(cell)} />
        </div>
        <BhFlowTitle clamp>{props.title || id}</BhFlowTitle>
        {prerequisites.length > 0 && (
          <BhFlowTags>
            {prerequisites.map(prerequisite => (
              <BhFlowTag key={prerequisite}>↳ after {prerequisite}</BhFlowTag>
            ))}
          </BhFlowTags>
        )}
      </BhFlowNode>
    </button>
  );
}

/**
 * Props:
 *   cells          — the mission's cell vertices
 *   dependsOnEdges — { from_id: dependent, to_id: prerequisite }
 *   selectedId     — the selected cell's element_id, or null
 *   onSelect       — (cellId) => void
 *   onOpen         — (cellId) => void
 *
 * Read-only by construction: no handler that writes is passed in, which is the same seam `canEdit`
 * already uses on the canvas. Wiring a dependency stays a graph affordance; here a prerequisite is
 * a chip.
 */
function MissionBoard({ cells, dependsOnEdges, selectedId, onSelect, onOpen }) {
  const lanes = buildLanes(cells, dependsOnEdges);
  const prerequisites = prerequisiteIndex(cells, dependsOnEdges);
  return (
    <div className="mc-lanes">
      {lanes.map(lane => (
        <div className="mc-lane" key={lane.key}>
          <SectionLabel>{lane.title} · {lane.cells.length}</SectionLabel>
          <div className="mc-lane-body">
            {lane.cells.map(cell => (
              <CellCard
                key={cell.element_id}
                cell={cell}
                prerequisites={prerequisites.get(cell.element_id) || []}
                selected={cell.element_id === selectedId}
                onSelect={onSelect}
                onOpen={onOpen}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default MissionBoard;
